import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import MedicineSaleReceiptModal from "./MedicineSaleReceiptModal";
import MedicineSaleEditModal from "./MedicineSaleEditModal";

// ✅ COMPACTED DYNAMIC STYLES FOR REACT SELECT
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 1px var(--theme-color, #0088cc)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    borderWidth: '1px',
    backgroundColor: 'white'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: '30px' }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '4px', fontSize: '11px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e0e6ed' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const MedicineSaleSearch = () => {
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    customer: ""
  });

  const [sales, setSales] = useState([]);
  const [saleMeta, setSaleMeta] = useState({ count: 0, totalAmount: 0 });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaleData, setEditSaleData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    setError(""); setSuccess(""); setLoading(true);
    try {
   
      const params = new URLSearchParams();
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.customer?.trim()) params.append("customer", filters.customer.trim());

     const res = await customFetch(`/MedicineSale/summarysearch?${params.toString()}`);

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      console.log("sssssss", data);
      const list = Array.isArray(data) ? data : data.data || [];

      setSales(list);
      setSaleMeta({ count: data.count || list.length, totalAmount: data.totalAmount || 0 });
      setSuccess(`Found ${data.count || list.length} sale(s). Net: ₹${data.totalAmount || 0}`);
      setCurrentPage(1); setExpandedRows(new Set());
    } catch (err) {
      setError(err.message); setSales([]); setSaleMeta({ count: 0, totalAmount: 0 });
    } finally { setLoading(false); }
  };

  const handleReset = () => {
    setFilters({ fromDate: new Date().toISOString().split("T")[0], toDate: new Date().toISOString().split("T")[0], customer: "" });
    setSales([]); setSaleMeta({ count: 0, totalAmount: 0 }); setError(""); setSuccess(""); setCurrentPage(1); setExpandedRows(new Set());
  };

  const handlePreview = async (billNo) => {
    try {
     const res = await customFetch(`/MedicineSale/receipt/${encodeURIComponent(billNo)}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setReceiptData(data); setShowReceipt(true);
    } catch (err) { setError(`Preview failed: ${err.message}`); }
  };

  const handleDelete = async (sale) => {
    if (!window.confirm(`Delete Sale: ${sale.billNo}?\n\nThis will reverse stock for ${sale.itemCount} items.`)) return;
    setError(""); setSuccess("");
    try {
     const res = await customFetch(`/MedicineSale/delete/${sale.billNo}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSales(prev => prev.filter((s) => s.billNo !== sale.billNo));
      setExpandedRows(prev => { const newSet = new Set(prev); newSet.delete(sale.billNo); return newSet; });
      setSuccess(`✅ ${data.message}: ${data.billNo}`);
    } catch (err) { setError(`❌ Delete failed: ${err.message}`); }
  };

  const openEditModal = async (sale) => {
    try {
    
    const res = await customFetch(`/MedicineSale/summarysearch?customer=${sale.billNo}`);
      const data = await res.json();
      const saleData = data.data.find((s) => s.billNo === sale.billNo);
      setEditSaleData(saleData); setShowEditModal(true);
    } catch (err) { setError("Failed to load edit data"); }
  };

  const toggleExpand = (billNo) => {
    setExpandedRows(prev => {
      const s = new Set(prev);
      if (s.has(billNo)) s.delete(billNo); else s.add(billNo);
      return s;
    });
  };

  const handleRowsPerPageChange = (e) => {
    let value = parseInt(e.target.value);
    if (isNaN(value) || value < 1) value = 10;
    if (value > 100) value = 100;
    setRowsPerPage(value); setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const effectiveRows = rowsPerPage || 10;
  const totalRecords = sales.length;
  const totalPages = Math.ceil(totalRecords / effectiveRows);
  const startIndex = (currentPage - 1) * effectiveRows;
  const endIndex = startIndex + effectiveRows;
  const currentSales = sales.slice(startIndex, endIndex);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

    return (
      <div className="pagination-controls">
        <button className="btn-page" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(num => (
            <button key={num} className={`btn-page ${currentPage === num ? "active" : ""}`} onClick={() => handlePageChange(num)}>{num}</button>
        ))}
        <button className="btn-page" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
      </div>
    );
  };

  return (
    <div className="sale-search-container">
      <div className="search-card">
        {/* Compact Header */}
        <div className="card-header">
          <div className="header-left">
             <div className="header-icon"><i className="bi bi-receipt"></i></div>
             <div className="header-text"><h5>Medicine Sale Search</h5><p>View and manage sales</p></div>
          </div>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="alert-custom alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          {/* Filters - Compact Grid */}
          <div className="search-section">
             <div className="filter-grid">
                <div className="form-group">
                   <label>From Date</label>
                   <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} className="form-input" />
                </div>
                <div className="form-group">
                   <label>To Date</label>
                   <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} className="form-input" />
                </div>
                <div className="form-group flex-2">
                   <label>Customer / Mobile</label>
                   <input type="text" name="customer" value={filters.customer} onChange={handleFilterChange} className="form-input" placeholder="Search..." />
                </div>
                <div className="form-group" style={{maxWidth: '80px'}}>
                   <label>Rows</label>
                   <input type="number" value={rowsPerPage} onChange={handleRowsPerPageChange} className="form-input" min="1" max="100" />
                </div>
                <div className="form-group search-btn-group">
                   <label>&nbsp;</label>
                   <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn-search" onClick={handleSearch} disabled={loading}>
                         {loading ? <i className="bi bi-arrow-clockwise spinning"></i> : <i className="bi bi-search"></i>} Search
                      </button>
                      <button className="btn-reset" onClick={handleReset}><i className="bi bi-x-lg"></i></button>
                   </div>
                </div>
             </div>
          </div>

          <div className="records-info">
             Showing <span className="fw-bold">{sales.length > 0 ? startIndex + 1 : 0}</span> to <span className="fw-bold">{Math.min(endIndex, totalRecords)}</span> of <span className="fw-bold">{totalRecords}</span> sales
          </div>

          {/* Table */}
          <div className="table-section">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th style={{width: '20px'}}></th>
                    <th style={{width: '40px'}}>Sr</th>
                    <th style={{width: '120px'}}>Bill No</th>
                    <th style={{width: '90px'}}>Date</th>
                    <th>Customer</th>
                    <th style={{width: '100px'}}>Mobile</th>
                    <th className="text-center" style={{width: '60px'}}>Items</th>
                    <th className="text-right" style={{width: '100px'}}>Net Amount</th>
                    <th style={{width: '90px'}}>Payment</th>
                    <th className="text-center" style={{width: '100px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="empty-state"><i className="bi bi-arrow-clockwise spinning"></i> Loading data...</td></tr>
                  ) : currentSales.length === 0 ? (
                    <tr><td colSpan="10" className="empty-state">No sales found.</td></tr>
                  ) : (
                    currentSales.map((sale, index) => (
                      <React.Fragment key={sale.billNo}>
                        <tr className={expandedRows.has(sale.billNo) ? "expanded-row" : ""}>
                          <td className="text-center toggle-cell" onClick={() => toggleExpand(sale.billNo)}>
                             <i className={`bi ${expandedRows.has(sale.billNo) ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                          </td>
                          <td className="text-muted fw-bold">{startIndex + index + 1}</td>
                          <td className="fw-medium text-primary">{sale.billNo}</td>
                          <td>{formatDate(sale.billDate)}</td>
                          <td>{sale.customer?.name || "-"}</td>
                          <td>{sale.customer?.mobileNo || "-"}</td>
                          <td className="text-center"><span className="badge-count">{sale.itemCount || sale.items?.length || 0}</span></td>
                          <td className="text-right fw-bold text-success">₹{(sale.netAmount ?? 0).toFixed(2)}</td>
                          <td>
                             <span className={`status-badge ${sale.paymentStatus === 'Paid' ? 'paid' : 'pending'}`}>
                                {sale.paymentStatus || "-"}
                             </span>
                          </td>
                          <td className="text-center">
                             <div className="btn-group-actions">
                                <button className="btn-icon preview" onClick={() => handlePreview(sale.billNo)} title="Receipt"><i className="bi bi-eye"></i></button>
                                <button className="btn-icon edit" onClick={() => openEditModal(sale)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                                <button className="btn-icon delete" onClick={() => handleDelete(sale)} title="Delete"><i className="bi bi-trash"></i></button>
                             </div>
                          </td>
                        </tr>

                        {/* Nested Table */}
                        {expandedRows.has(sale.billNo) && sale.items && (
                          <tr className="nested-row">
                            <td colSpan="10" className="p-0">
                               <div className="nested-table-container">
                                  <table className="nested-table">
                                     <thead>
                                        <tr>
                                           <th>Medicine</th>
                                           <th style={{width: '120px'}}>Batch</th>
                                           <th style={{width: '60px'}}>Qty</th>
                                           <th style={{width: '80px'}}>MRP</th>
                                           <th style={{width: '80px'}}>Rate</th>
                                           <th style={{width: '80px'}}>Taxable</th>
                                           <th className="text-right" style={{width: '90px'}}>Total</th>
                                        </tr>
                                     </thead>
                                     <tbody>
                                        {sale.items.map((item, idx) => (
                                           <tr key={item.pid || idx}>
                                              <td className="fw-medium">{item.medicine}</td>
                                              <td className="text-muted">{item.batchNo || "-"}</td>
                                              <td>{item.qty || item.salePcs || 0}</td>
                                              <td>₹{item.mrp || 0}</td>
                                              <td>₹{item.sRate || 0}</td>
                                              <td>₹{item.taxable ?? 0}</td>
                                              <td className="text-right fw-bold">₹{(item.netAmount || 0).toFixed(2)}</td>
                                           </tr>
                                        ))}
                                     </tbody>
                                  </table>
                               </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && <div className="pagination-section">{renderPagination()}</div>}

        </div>
      </div>

      {showEditModal && editSaleData && (
        <MedicineSaleEditModal show={showEditModal} saleData={editSaleData} onClose={() => { setShowEditModal(false); setEditSaleData(null); }} onUpdate={handleSearch} />
      )}
      {showReceipt && receiptData && (
        <MedicineSaleReceiptModal show={showReceipt} receipt={receiptData} onClose={() => setShowReceipt(false)} onPrint={() => window.print()} />
      )}

      {/* COMPACT STYLES */}
      <style jsx>{`
        .sale-search-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
        .search-card { background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; overflow: hidden; }
        
        /* Header */
        .card-header { background: var(--theme-color, #0088cc); padding: 8px 12px; border-bottom: 2px solid #00d4aa; }
        .header-left { display: flex; align-items: center; gap: 8px; color: white; }
        .header-icon { width: 26px; height: 26px; background: rgba(255, 255, 255, 0.2); border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 14px; }
        .header-text h5 { margin: 0; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .card-content { padding: 12px; }

        /* Alerts */
        .alert-custom { margin-bottom: 12px; padding: 6px 10px; border-radius: 4px; display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }

        /* Filter Grid */
        .search-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px; margin-bottom: 12px; }
        .filter-grid { display: grid; grid-template-columns: 1fr 1fr 2fr auto auto; gap: 10px; align-items: end; }
        
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .form-group label { font-size: 11px; font-weight: 600; color: #4a5568; white-space: nowrap; }
        
        .form-input { padding: 0 8px; height: 30px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; outline: none; width: 100%; transition: 0.2s; background: white; }
        .form-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 1px var(--theme-color, #0088cc); }

        .search-btn-group { display: flex; align-items: center; gap: 6px; }
        .btn-search { padding: 0 12px; height: 30px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-weight: 600; font-size: 11px; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: 0.2s; }
        .btn-search:hover:not(:disabled) { opacity: 0.9; }
        .btn-reset { width: 30px; height: 30px; background: #edf2f7; color: #4a5568; border: 1px solid #cbd5e0; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; font-size: 12px; }
        .btn-reset:hover { background: #e2e8f0; }

        .records-info { font-size: 11px; color: #718096; margin-bottom: 8px; text-align: right; }

        /* Main Table */
        .table-section { border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; text-align: left; }
        .custom-table th { background: #edf2f7; color: #4a5568; padding: 8px 10px; font-weight: 600; text-transform: uppercase; white-space: nowrap; border-bottom: 1px solid #cbd5e0; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; color: #2d3748; white-space: nowrap; }
        .custom-table tr:hover { background: #f8fafc; }
        
        .toggle-cell { cursor: pointer; color: var(--theme-color, #0088cc); font-size: 12px; font-weight: bold; padding: 0 !important; }
        .badge-count { background: #ebf8ff; color: #3182ce; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-size: 10px; }
        .expanded-row { background-color: #f0f9ff !important; }
        
        .status-badge { padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 600; }
        .status-badge.paid { background: #c6f6d5; color: #22543d; }
        .status-badge.pending { background: #fefcbf; color: #744210; }

        /* Nested Table */
        .nested-row td { background: #f8fafc; padding: 0; border-bottom: 1px solid #e2e8f0; }
        .nested-table-container { padding: 8px 12px 12px 30px; border-left: 3px solid var(--theme-color, #0088cc); background: #f8fafc; }
        .nested-table { width: 100%; font-size: 11px; border: 1px solid #e2e8f0; border-radius: 4px; background: white; }
        .nested-table th { background: var(--theme-color, #0088cc); color: white; padding: 6px 8px; font-weight: 600; }
        .nested-table td { padding: 4px 8px; border-bottom: 1px solid #e2e8f0; }

        /* Typography & Utilities */
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 700; }
        .fw-medium { font-weight: 500; }
        .text-success { color: #2f855a; }
        .text-primary { color: var(--theme-color, #3182ce); }
        .text-muted { color: #718096; }
        .p-0 { padding: 0 !important; }
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Actions */
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
        .btn-icon { width: 22px; height: 22px; border-radius: 4px; border: none; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: 0.2s; }
        .btn-icon.preview { background: #ebf8ff; color: #0088cc; }
        .btn-icon.preview:hover { background: #bee3f8; }
        .btn-icon.edit { background: #c6f6d5; color: #2f855a; }
        .btn-icon.edit:hover { background: #9ae6b4; }
        .btn-icon.delete { background: #fff5f5; color: #e53e3e; }
        .btn-icon.delete:hover { background: #fed7d7; }
        .empty-state { text-align: center; padding: 30px; color: #a0aec0; font-style: italic; }

        /* Pagination */
        .pagination-section { display: flex; justify-content: center; margin-top: 12px; }
        .pagination-controls { display: flex; gap: 4px; }
        .btn-page { width: 26px; height: 26px; border: 1px solid #e2e8f0; background: white; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #4a5568; transition: 0.2s;}
        .btn-page:hover:not(:disabled) { background: #edf2f7; }
        .btn-page.active { background: var(--theme-color, #3182ce); color: white; border-color: var(--theme-color, #3182ce); font-weight: 600; }
        .btn-page:disabled { opacity: 0.4; cursor: not-allowed; }

        /* Responsive Breakpoints */
        @media (max-width: 992px) {
          .filter-grid { grid-template-columns: 1fr 1fr; }
          .flex-2 { grid-column: span 2; }
          .search-btn-group { grid-column: span 2; justify-content: flex-end; }
        }
        @media (max-width: 576px) {
          .filter-grid { grid-template-columns: 1fr; }
          .flex-2 { grid-column: span 1; }
          .search-btn-group { grid-column: span 1; justify-content: stretch; }
          .btn-search { flex: 1; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default MedicineSaleSearch;